import {Component, inject, OnInit, OnDestroy} from '@angular/core';
import {RouterModule, Router} from '@angular/router';
import {CommonModule} from '@angular/common';
import {KeycloakService} from './services/keycloak.service';
import {ThemeService} from './services/theme.service';
import {BrandingService} from './services/branding.service';
import {OidcSecurityService} from "angular-auth-oidc-client";
import {Header} from "./components/header/header";
import {Toast} from "./components/toast/toast";
import {ImpersonationBannerComponent} from "./components/impersonation-banner/impersonation-banner.component";
import {TourOverlayComponent} from "./components/tour/tour-overlay/tour-overlay.component";
import {ApiService} from "./services/api.service";
import {NotificationService} from "./services/notification.service";
import {TourService} from "./services/tour.service";
import {firstValueFrom} from "rxjs";
import {User, UserRole, UserStatus} from "./interfaces/api-interfaces";
import {jwtDecode} from "jwt-decode";
import {environment} from "../environments/environment";

// JWT Token structure from Keycloak
interface KeycloakJwtPayload {
    // Legacy custom attribute: string or string[]. Native Keycloak Organizations
    // membership (Organizations tab, "Join organization"): object keyed by org name,
    // e.g. { "FreyMuT": {} }.
    organization?: string | string[] | Record<string, unknown>;
    realm_access?: {
        roles: string[]; // Raw string roles from JWT
    };
}

@Component({
    imports: [RouterModule, CommonModule, Header, Toast, ImpersonationBannerComponent, TourOverlayComponent],
    selector: 'app-root',
    templateUrl: './app.html',
    styleUrl: './app.css',
})
export class App implements OnInit, OnDestroy {
    protected readonly keycloakService = inject(KeycloakService);
    private readonly oidcSecurityService = inject(OidcSecurityService);
    private readonly themeService = inject(ThemeService);
    private readonly brandingService = inject(BrandingService);
    private readonly apiService = inject(ApiService);
    private readonly notificationService = inject(NotificationService);
    private readonly tourService = inject(TourService);

    private readonly router = inject(Router);

    protected get showSupportButton(): boolean {
        const roles = this.keycloakService.currentUser()?.roles ?? [];
        const isCommunityPage = this.router.url.startsWith('/community');
        return !isCommunityPage && !roles.includes(UserRole.MASASANA) && !roles.includes(UserRole.FREYMUT);
    }

    // Environment banner properties
    protected readonly isProduction = environment.production;
    protected readonly isStaging = (environment as any).staging;
    protected readonly apiBaseUrl = this.apiService.baseUrl;

    constructor() {
        // Initialize theme and branding on app startup
        this.themeService.initializeTheme();
        this.brandingService.fetchBranding();
    }

    ngOnInit() {
        this.oidcSecurityService.checkAuth().subscribe({
            next: async ({isAuthenticated, userData, accessToken}) => {
                if (isAuthenticated) {
                    const decoded = jwtDecode<KeycloakJwtPayload>(accessToken);

                    // Log raw JWT data for debugging
                    console.log('JWT Decoded:', {
                        organization: decoded.organization,
                        rawRoles: decoded.realm_access?.roles || [],
                        allowedRoles: this.keycloakService.allowedRoles
                    });

                    if (!decoded.realm_access?.roles || decoded.realm_access.roles.length === 0) {
                        console.error('ROLE EXTRACTION ERROR: No roles found in JWT token', {
                            jwtStructure: decoded,
                            expectedPath: 'realm_access.roles'
                        });
                    }

                    const keycloakUser = this.conformKeycloakUser(userData);
                    // Handle organization claim, which differs by how the user was assigned:
                    // - legacy custom attribute: string or string[] (e.g. "FreyMuT" or ["FreyMuT"])
                    // - native Keycloak Organizations membership: object keyed by org name
                    //   (e.g. { "FreyMuT": {} }) — must extract the keys, not wrap the object itself.
                    if (Array.isArray(decoded.organization)) {
                        keycloakUser.organization = decoded.organization;
                    } else if (decoded.organization && typeof decoded.organization === 'object') {
                        keycloakUser.organization = Object.keys(decoded.organization);
                    } else if (decoded.organization) {
                        keycloakUser.organization = [decoded.organization];
                    } else {
                        keycloakUser.organization = [];
                    }

                    // Map Keycloak roles to application roles
                    const roleMapping: { [key: string]: UserRole } = {
                        'role_masasana': UserRole.MASASANA,
                        'role_freymut': UserRole.FREYMUT,
                        'role_dozent': UserRole.DOZENT,
                        'role_teilnehmer': UserRole.TEILNEHMER,
                        'role_absolvent': UserRole.ABSOLVENT,
                    };

                    // Case-insensitive role filtering with mapping
                    const jwtRoles = decoded.realm_access?.roles || [];
                    const normalizedJwtRoles = jwtRoles.map((r: string) => r.toLowerCase());
                    const filteredRoles = normalizedJwtRoles
                        .map((role: string) => roleMapping[role])
                        .filter((role): role is UserRole => role !== undefined);

                    console.log('Role Filtering Result:', {
                        jwtRoles,
                        normalizedJwtRoles,
                        filteredRoles,
                        wasFiltered: jwtRoles.length > filteredRoles.length
                    });

                    if (filteredRoles.length === 0 && jwtRoles.length > 0) {
                        console.warn('ROLE FILTERING WARNING: All roles were filtered out', {
                            jwtRoles,
                            normalizedJwtRoles,
                            allowedRoles: this.keycloakService.allowedRoles,
                            hint: 'Check for case sensitivity or role name mismatches'
                        });
                    }

                    keycloakUser.roles = filteredRoles;
                    console.log('Final user roles:', keycloakUser.roles);

                    this.keycloakService.updateUser(keycloakUser);
                    await this.getAuthMe();
                }
                // Let the route guards handle authentication - don't call authorize() here
            },
            error: (error) => {
                console.error('Authentication check failed:', error);
            }
        });
    }

    async getAuthMe() {
        try {
            const apiUser = await firstValueFrom(this.apiService.getUserProfile());
            const currentUser = this.keycloakService.currentUser();
            // Merge API user data with existing Keycloak user data to ensure all fields are populated
            const updatedUser = {
                ...currentUser!,
                ...apiUser,
                // Preserve Keycloak-derived identity fields if API returns empty values
                fullName: apiUser.fullName || currentUser!.fullName,
                username: apiUser.username || currentUser!.username,
                firstName: apiUser.firstName || currentUser!.firstName,
                lastName: apiUser.lastName || currentUser!.lastName,
                // Preserve roles and organization from Keycloak token
                roles: currentUser!.roles,
                organization: currentUser!.organization
            };
            this.keycloakService.updateUser(updatedUser);

            // Initialize theme from user data after profile loads
            this.themeService.initializeFromUser(updatedUser);

            // Start the guided onboarding tour (needs the API-merged `onboarding`
            // field, not the raw JWT user — see TourService.startFromUser).
            this.tourService.startFromUser(updatedUser);

            // Start notification polling after successful authentication
            this.notificationService.startPolling();
        } catch (e) {
            console.error('Failed to load user profile from API:', e);
            // Continue with Keycloak user data if API fails
            // Still try to initialize theme from whatever user data we have
            const currentUser = this.keycloakService.currentUser();
            this.themeService.initializeFromUser(currentUser);

            // Start notification polling even if API profile load fails
            this.notificationService.startPolling();
        }
    }

    conformKeycloakUser(userData: any){
        const firstName = userData.given_name || '';
        const lastName = userData.family_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return {
            userId: userData.sub,
            keycloakId: userData.sub,
            roles: [] as UserRole[],
            email: userData.email,
            status: "active",
            username: userData.preferred_username,
            fullName: fullName || userData.preferred_username || userData.email,
            firstName,
            lastName,
            displayUsername: userData.display_name,
        } as User
    }

    ngOnDestroy() {
        // Stop notification polling when app is destroyed
        this.notificationService.stopPolling();
    }
}
